import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Glass, Mono } from "@/components/network/NetworkAtoms";

const CHANNELS = ["TikTok ads", "Meta ads", "LinkedIn ads", "YouTube ads", "Google ads", "Email & newsletters",
                  "Content & SEO", "Affiliate network", "Podcast & radio", "Something else"];
const AGENTS = ["1 – 5", "6 – 20", "21 – 50", "50+"];
const REACH = ["Under 10k", "10k – 50k", "50k – 250k", "250k+"];

const dark = "bg-white/[0.04] border-white/12 text-white placeholder:text-slate-500 focus:border-[#0055FF] focus-visible:ring-0";

function Chips({ options, value, onPick, multi = false, testId }) {
  const picked = (o) => (multi ? value.includes(o) : value === o);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onPick(o)} aria-pressed={picked(o)}
                data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`h-9 rounded-full border px-3.5 text-[12.5px] font-medium transition-colors ${
                  picked(o) ? "bg-[#0055FF]/[0.16] border-[#0055FF] text-white" : "border-white/12 text-slate-300 hover:border-white/30"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

const FieldLabel = ({ children, hint }) => (
  <div className="flex items-baseline justify-between gap-3 mb-2">
    <Mono className="!text-slate-400">{children}</Mono>
    {hint && <span className="text-[11.5px] text-slate-500">{hint}</span>}
  </div>
);

/** Create the account now; the desk approves before the first case. Signs the partner in on success. */
export function PartnerSignupForm({ lane, setLane, refId, refName }) {
  const nav = useNavigate();
  const { adopt } = useAuth();
  const [kind, setKind] = useState(lane === "media" ? "media" : "call_centre");
  useEffect(() => { setKind(lane === "media" ? "media" : "call_centre"); }, [lane]);
  const pick = (k) => { setKind(k); setLane(k); };

  const [f, setF] = useState({ company: "", name: "", email: "", phone: "", website: "", scale: "", password: "", confirm: "" });
  const [channels, setChannels] = useState([]);
  const [consent, setConsent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleChannel = (c) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const pwWeak = f.password.length > 0 && f.password.length < 8;
  const mismatch = f.confirm.length > 0 && f.confirm !== f.password;

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("Choose a password of at least 8 characters");
    if (f.password !== f.confirm) return toast.error("The two passwords don't match");
    if (!consent) return toast.error("Please tick the box so we can contact you about the partnership");
    setBusy(true);
    try {
      const { data } = await api.post("/partner/signup", {
        kind, company: f.company.trim(), name: f.name.trim(), email: f.email.trim(), password: f.password,
        phone: f.phone.trim() || null, website: f.website.trim() || null, scale: f.scale || null,
        channels: kind === "media" && channels.length ? channels : null,
        referred_by: refId || null, consent: true,
      });
      adopt(data.token, data.user);
      toast.success("Your account is set up — welcome to Connexis");
      nav("/introducer", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail) || "Couldn't create the account — try again");
    } finally { setBusy(false); }
  };

  return (
    <Glass className="p-6 sm:p-8" hover={false} testId="pwu-signup-form">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <FieldLabel>You are</FieldLabel>
          <div className="grid grid-cols-2 gap-2">
            {[["call_centre", "A call centre"], ["media", "Media & advertising"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => pick(k)} data-testid={`pwu-signup-kind-${k}`} aria-pressed={k === kind}
                      className={`h-10 rounded-full border text-[12.5px] font-semibold transition-colors ${
                        k === kind ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-white/12 text-slate-300 hover:border-white/30"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Input required value={f.company} onChange={set("company")} placeholder="Company" className={`h-11 ${dark}`} data-testid="pwu-signup-company" />
          <Input required value={f.name} onChange={set("name")} placeholder="Your name" className={`h-11 ${dark}`} data-testid="pwu-signup-name" />
          <Input required type="email" value={f.email} onChange={set("email")} placeholder="Work email — this is your sign-in" className={`h-11 ${dark}`} data-testid="pwu-signup-email" />
          <Input value={f.phone} onChange={set("phone")} placeholder="Mobile (optional)" className={`h-11 ${dark}`} data-testid="pwu-signup-phone" />
          <Input value={f.website} onChange={set("website")} placeholder="Website (optional)" className={`h-11 ${dark} sm:col-span-2`} data-testid="pwu-signup-website" />
        </div>

        {kind === "media" ? (
          <div data-testid="pwu-signup-media-fields">
            <FieldLabel hint="pick every channel you run">Channels you run</FieldLabel>
            <Chips options={CHANNELS} value={channels} onPick={toggleChannel} multi testId="pwu-signup-channel" />
            <div className="mt-4">
              <FieldLabel hint="optional">Monthly reach</FieldLabel>
              <Chips options={REACH} value={f.scale} onPick={(o) => setF({ ...f, scale: f.scale === o ? "" : o })} testId="pwu-signup-scale" />
            </div>
          </div>
        ) : (
          <div data-testid="pwu-signup-centre-fields">
            <FieldLabel hint="roughly is fine">Agents on the floor</FieldLabel>
            <Chips options={AGENTS} value={f.scale} onPick={(o) => setF({ ...f, scale: f.scale === o ? "" : o })} testId="pwu-signup-scale" />
          </div>
        )}

        <div>
          <FieldLabel hint="at least 8 characters">Choose a password</FieldLabel>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="relative">
              <Input required type={showPw ? "text" : "password"} value={f.password} onChange={set("password")} placeholder="Password"
                     autoComplete="new-password" className={`h-11 pr-11 ${dark} ${pwWeak ? "!border-amber-400/60" : ""}`} data-testid="pwu-signup-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid="pwu-signup-password-toggle">
                {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <Input required type={showPw ? "text" : "password"} value={f.confirm} onChange={set("confirm")} placeholder="Type it again"
                   autoComplete="new-password" className={`h-11 ${dark} ${mismatch ? "!border-amber-400/60" : ""}`} data-testid="pwu-signup-confirm" />
          </div>
          {mismatch && <p className="mt-2 text-[12px] text-amber-300" data-testid="pwu-signup-mismatch">Those two don&apos;t match yet.</p>}
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="pwu-signup-consent">
          <span className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
            consent ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-white/20"}`}>
            {consent && <Check className="size-3.5" />}
          </span>
          <input type="checkbox" className="sr-only" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="pwu-signup-consent-input" />
          <span className="text-[12.5px] leading-relaxed text-slate-400">
            Connexis can contact me about the partnership. I understand the desk reviews every new partner before the first
            case, and that commission is paid on funded deals against a signed agreement.
            {refName && <> I was introduced by <span className="text-white font-medium">{refName}</span>.</>}
          </span>
        </label>

        <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
          <p className="flex items-center gap-1.5 text-[11.5px] text-slate-500"><ShieldCheck className="size-3.5 text-emerald-400/80" /> You can sign in straight away. Cases and links unlock on approval.</p>
          <Button type="submit" disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] h-11 px-6 font-semibold" data-testid="pwu-signup-submit">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <>Create my account <ArrowRight className="size-4 ml-1" /></>}
          </Button>
        </div>
      </form>
    </Glass>
  );
}
